import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity } from "react-native";
import { AppIcon, AppText } from "@toothfairy/shared-ui";

const ENTITY_TYPE_CONFIG = {
  agent: {
    icon: AppAssets?.icons?.taskAgent,
    color: "#16a34a",
    labelKey: "settings.agents",
  },
  tool: {
    icon: AppAssets?.icons?.tools,
    color: "#6366f1",
    labelKey: "tools.title",
  },
  hook: {
    icon: AppAssets?.icons?.parser,
    color: "#8b5cf6",
    labelKey: "hooks.title",
  },
  authorisation: {
    icon: AppAssets?.icons?.lock,
    color: "#d97706",
    labelKey: "authorisations.title",
  },
};

const RelationshipChip = ({ item, currentTheme }) => {
  const { t } = useTranslation();
  const config = ENTITY_TYPE_CONFIG[item.type] || ENTITY_TYPE_CONFIG.tool;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 6,
        paddingHorizontal: 10,
        marginHorizontal: 4,
        marginVertical: 3,
        backgroundColor: `${config.color}10`,
        borderRadius: 6,
        borderWidth: 0.5,
        borderColor: `${config.color}30`,
      }}
    >
      <AppIcon
        icon={config.icon}
        color={config.color}
        size={12}
        style={{ marginRight: 6 }}
      />
      <AppText
        style={{
          fontSize: 13,
          color: currentTheme?.theme?.font_color,
          fontWeight: "500",
          maxWidth: 220,
        }}
        numberOfLines={1}
      >
        {item.name}
      </AppText>
      <AppText
        style={{
          fontSize: 10,
          color: config.color,
          marginLeft: 6,
          fontStyle: "italic",
        }}
      >
        {t(config.labelKey)}
      </AppText>
    </View>
  );
};

const RelationshipSection = ({
  title,
  items,
  emptyMessage,
  icon,
  currentTheme,
  t,
}) => {
  if (!items || items.length === 0) {
    return (
      <AppExpander
        style={{ minWidth: 250, zIndex: -1, marginTop: 10 }}
        title={`${title} (0)`}
        icon={icon}
        isExpandedOnLoad={false}
        isEnabled={false}
      >
        <View style={{ padding: 10 }}>
          <AppText
            style={{
              fontSize: 13,
              color: currentTheme?.theme?.darkGrey,
              fontStyle: "italic",
            }}
          >
            {emptyMessage}
          </AppText>
        </View>
      </AppExpander>
    );
  }

  return (
    <AppExpander
      style={{ minWidth: 250, zIndex: -1, marginTop: 10 }}
      title={`${title} (${items.length})`}
      icon={icon}
      isExpandedOnLoad={false}
    >
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          paddingVertical: 4,
          paddingHorizontal: 6,
        }}
      >
        {items.map((item, idx) => (
          <RelationshipChip
            key={`${item.id}-${idx}`}
            item={item}
            currentTheme={currentTheme}
          />
        ))}
      </View>
    </AppExpander>
  );
};

const RelationshipPanel = ({ dependsOn = [], usedBy = [], isEdit = false }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();

  const memoizedDependsOn = useMemo(
    () => dependsOn.filter((item) => item && item.id && item.name),
    [JSON.stringify(dependsOn)],
  );

  const memoizedUsedBy = useMemo(
    () => usedBy.filter((item) => item && item.id && item.name),
    [JSON.stringify(usedBy)],
  );

  if (!isEdit) return null;

  const hasDependsOn = memoizedDependsOn.length > 0;
  const hasUsedBy = memoizedUsedBy.length > 0;

  if (!hasDependsOn && !hasUsedBy) {
    return (
      <View style={{ zIndex: -1, marginTop: 10 }}>
        <AppExpander
          style={{ minWidth: 250, zIndex: -1 }}
          title={t("common.relationships")}
          icon={AppAssets?.icons?.network}
          isExpandedOnLoad={false}
          isEnabled={false}
        >
          <View style={{ padding: 10 }}>
            <AppText
              style={{
                fontSize: 13,
                color: currentTheme?.theme?.darkGrey,
                fontStyle: "italic",
              }}
            >
              {t("common.noDependencies")}
            </AppText>
          </View>
        </AppExpander>
      </View>
    );
  }

  return (
    <View style={{ zIndex: -1 }}>
      {hasDependsOn && (
        <RelationshipSection
          title={t("common.dependsOn")}
          items={memoizedDependsOn}
          emptyMessage={t("common.noDependencies")}
          icon={AppAssets?.icons?.network}
          currentTheme={currentTheme}
          t={t}
        />
      )}
      {hasUsedBy && (
        <RelationshipSection
          title={t("common.usedBy")}
          items={memoizedUsedBy}
          emptyMessage={t("common.notUsedByAny")}
          icon={AppAssets?.icons?.consumed}
          currentTheme={currentTheme}
          t={t}
        />
      )}
      {!hasDependsOn && (
        <RelationshipSection
          title={t("common.dependsOn")}
          items={[]}
          emptyMessage={t("common.noDependencies")}
          icon={AppAssets?.icons?.network}
          currentTheme={currentTheme}
          t={t}
        />
      )}
      {!hasUsedBy && (
        <RelationshipSection
          title={t("common.usedBy")}
          items={[]}
          emptyMessage={t("common.notUsedByAny")}
          icon={AppAssets?.icons?.consumed}
          currentTheme={currentTheme}
          t={t}
        />
      )}
    </View>
  );
};

export default RelationshipPanel;
